import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { domain } from '../common/domain';
import { useNavigate } from 'react-router-dom';

const Billing = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [totalAmount, setTotalAmount] = useState(0);
  const [lastChanged, setLastChanged] = useState(null);
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [customTotalAmount, setCustomTotalAmount] = useState('');

  // Update currentDateTime every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Format date and time
  const formatDateTime = (date) => {
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const defaultItems = {
    categoryId: '',
    subCategoryId: '',
    productId: '',
    rate: '',
    counter: '',
    salesPerson: '',
    weight: '',
    wastagePercent: '',
    wastageAmt: '',
    quantity: 1,
    total: 0,
    orginal_price: '',
  }
  const [newItem, setNewItem] = useState(defaultItems);

  const fetchData = async (endpoint, setter) => {
    try {
      const res = await axios.get(`${domain()}?endpoint=${endpoint}`);
      setter(res.data);
    } catch (err) {
      console.error('Fetch error:', err);
    }
  };

  useEffect(() => {
    fetchData('categories', setCategories);
    fetchData('subcategories', setSubCategories);
  }, []);

  useEffect(() => {
    if (newItem.categoryId && newItem.subCategoryId) {
      fetchData(
        `products_details&category_id=${newItem.categoryId}&sub_category_id=${newItem.subCategoryId}`,
        setProducts
      );
    }
  }, [newItem.categoryId, newItem.subCategoryId]);

  useEffect(() => {
    if (selectedProduct) {
      setNewItem(prev => ({
        ...prev,
        counter: selectedProduct.counter_name,
        rate: selectedProduct.rate,
        weight: selectedProduct.weight,
        salesperson_id: selectedProduct.salesperson_id,
        orginal_price: selectedProduct.orginal_price || '',
      }));
    }
  }, [selectedProduct]);

  useEffect(() => {
    const rate = parseFloat(newItem.rate || 0);
    const weight = parseFloat(newItem.weight || 0);
    const quantity = parseInt(newItem.quantity || 1);
    const base = weight * rate;

    if (lastChanged === 'percent') {
      const percent = parseFloat(newItem.wastagePercent || 0);
      const wastageAmt = (base * percent) / 100;
      setNewItem(prev => ({
        ...prev,
        wastageAmt: wastageAmt.toFixed(2),
        total: ((base + wastageAmt) * quantity).toFixed(2)
      }));
    } else if (lastChanged === 'amount') {
      const amt = parseFloat(newItem.wastageAmt || 0);
      const percent = base > 0 ? (amt / base) * 100 : 0;
      setNewItem(prev => ({
        ...prev,
        wastagePercent: percent.toFixed(2),
        total: ((base + amt) * quantity).toFixed(2)
      }));
    } else if (lastChanged === 'total') {
      const total = parseFloat(newItem.total || 0);
      const wastageAmt = (total / quantity) - base;
      const percent = base > 0 ? (wastageAmt / base) * 100 : 0;
      setNewItem(prev => ({
        ...prev,
        wastageAmt: wastageAmt.toFixed(2),
        wastagePercent: percent.toFixed(2)
      }));
    } else {
      const wastageAmt = parseFloat(newItem.wastageAmt || 0);
      const total = (base + wastageAmt) * quantity;
      setNewItem(prev => ({
        ...prev,
        total: total.toFixed(2)
      }));
    }
  }, [
    newItem.rate,
    newItem.weight,
    newItem.quantity,
    newItem.wastagePercent,
    newItem.wastageAmt,
    newItem.total,
    lastChanged,
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const cleanedValue =
      ['weight', 'wastagePercent', 'wastageAmt', 'total', 'rate'].includes(name)
        ? value.replace(/[^0-9.]/g, '')
        : ['quantity'].includes(name)
        ? value.replace(/[^0-9]/g, '')
        : value;

    setNewItem(prev => ({
      ...prev,
      [name]: cleanedValue
    }));

    if (name === 'wastagePercent') setLastChanged('percent');
    else if (name === 'wastageAmt') setLastChanged('amount');
    else if (name === 'total') setLastChanged('total');
    else setLastChanged(null);
  };
  const [billItem, setBillItem] = useState([]);
  const [advanceAmount, setAdvanceAmount] = useState(0)
  const handleAddItem = () => {
    if(selectedProduct){
      setBillItem((prev) => [...prev,{
        ...newItem,
        productName: selectedProduct.name
      }]);
      setNewItem(defaultItems);
      setSelectedProduct(null);
    }
  };
  const handleDeleteItem = (indexToDelete) => {
    setBillItem(prev => prev.filter((_, index) => index !== indexToDelete));
  };
    const handleBillSubmit = async () => {
      const mappedItems = billItem.map(item => ({
        product_id: parseInt(item.productId),
        category_id: parseInt(item.categoryId),
        sub_category_id: parseInt(item.subCategoryId),
        rate: parseFloat(item.rate),
        counter: item.counter || 'A',
        salesperson_id: item.salesperson_id ? parseInt(item.salesperson_id) : 1,
        weight: parseFloat(item.weight),
        wastage_percentage: parseFloat(item.wastagePercent),
        wastage_amount: parseFloat(item.wastageAmt),
        quantity: parseInt(item.quantity),
        total_amount: parseFloat(item.total),
        orginal_price: parseFloat(item.orginal_price)
      }));
    
      // Use custom total amount if provided, else calculate
      const totalAmount = customTotalAmount !== '' ? parseFloat(customTotalAmount) : mappedItems.reduce((sum, item) => sum + item.total_amount, 0);
      const advanceAmt = parseFloat(advanceAmount) || 0;
      const finalTotal = totalAmount - advanceAmt;
    
      const payload = {
        total_amount: totalAmount,
        advance_amount: advanceAmt,
        final_total: finalTotal,
        items: mappedItems
      };
    
    try {
      const response = await axios.post(domain() + '?endpoint=bills', payload);
      if (response.data.success) {
        // Clear everything
        setBillItem([]);
        setNewItem(defaultItems);
        setSelectedProduct(null);
        setAdvanceAmount(0);
        setTotalAmount(0);
        
        // Get today's date in YYYY-MM-DD format
        const today = new Date().toISOString().split('T')[0];
        
        // Use React Router's navigate instead of window.location
        navigate(`/billing-report?start_date=${today}&end_date=${today}`);
      }
    } catch (error) {
      console.error('Error while creating bill:', error);
      alert('Error creating bill. Please try again.');
    }
  }
  const getAvailableStock = () => {
    if (!selectedProduct) return 0;
    return selectedProduct.quantity || 0;
  };
  const isStockInsufficient = selectedProduct && parseInt(newItem.quantity || 0) > getAvailableStock();
  return (
    <div className="p-3 bg-light text-dark rounded" style={{ maxWidth: 900, margin: '0 auto' }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5>Billing</h5>
        <div className="text-end">
          <div className="fw-bold">Date & Time</div>
          <div>{formatDateTime(currentDateTime)}</div>
        </div>
      </div>

      <div className="row g-2 mb-3">
        {[
          { label: 'Category', name: 'categoryId', options: categories },
          { label: 'Subcategory', name: 'subCategoryId', options: subCategories },
        ].map(({ label, name, options }) => (
          <div className="col" key={name}>
            <label>{label}</label>
            <select
              className="form-select"
              name={name}
              value={newItem[name]}
              onChange={handleChange}
            >
              <option value="">Select {label}</option>
              {options.map(opt => (
                <option key={opt.id} value={opt.id}>{opt.name}</option>
              ))}
            </select>
          </div>
        ))}

        <div className="col">
          <label>Product</label>
          <select
            className="form-select"
            name="productId"
            value={newItem.productId}
            onChange={(e) => {
              handleChange(e);
              setSelectedProduct(products.find(p => p.id == e.target.value));
            }}
          >
            <option value="">Select Product</option>
            {products.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="row g-2 mb-3">
        <div className="col"><label>Purity</label><input className="form-control" value={selectedProduct?.purity_name || ''} disabled /></div>
        <div className="col"><label>Rate</label><input className="form-control" name="rate" value={newItem.rate} onChange={handleChange} disabled={!selectedProduct} /></div>
        <div className="col"><label>Counter</label><input className="form-control" value={selectedProduct?.counter_name || ''} disabled name='counter' onChange={handleChange}/></div>
        <div className="col"><label>Sales Person</label><input className="form-control" value={selectedProduct?.salesperson_name || ''} disabled /></div>
        <div className="col"><label>Sales Person ID</label><input className="form-control" value={selectedProduct?.salesperson_id || ''} disabled name='salesperson_id' onChange={handleChange}/></div>
      </div>

      <div className="row g-2 mb-3">
        <div className="col"><label>Weight</label><input className="form-control" name="weight" value={newItem.weight} onChange={handleChange} disabled={!selectedProduct || !newItem.rate || parseFloat(newItem.rate) <= 0} /></div>
        <div className="col"><label>Wastage %</label><input className="form-control" name="wastagePercent" value={newItem.wastagePercent} onChange={handleChange} disabled={!selectedProduct || !newItem.rate || parseFloat(newItem.rate) <= 0} /></div>
        <div className="col"><label>Wastage Amt</label><input className="form-control" name="wastageAmt" value={newItem.wastageAmt} onChange={handleChange} disabled={!selectedProduct || !newItem.rate || parseFloat(newItem.rate) <= 0} /></div>
        <div className="col"><label>Quantity</label><input className="form-control" name="quantity" value={newItem.quantity} onChange={handleChange} disabled={!selectedProduct || !newItem.rate || parseFloat(newItem.rate) <= 0} /></div>
        {isStockInsufficient && (
          <div className="col-12 text-danger fw-bold">Requested quantity exceeds available stock ({getAvailableStock()})!</div>
        )}
        <div className="col"><label>Orginal Price</label><input className="form-control" name="orginal_price" value={newItem.orginal_price} onChange={handleChange} disabled={!selectedProduct} /></div>
      </div>

      <button 
        className="btn btn-primary mb-3" 
        onClick={handleAddItem} 
        disabled={
          !selectedProduct || 
          !newItem.rate || 
          parseFloat(newItem.rate) <= 0 || 
          !newItem.weight || 
          !newItem.quantity || 
          !newItem.total ||
          isStockInsufficient
        }
      >
        Add to Bill
      </button>

      <table className="table table-bordered text-center">
        <thead>
          <tr>
            <th>#</th>
            <th>Product</th>
            <th>Weight</th>
            <th>Wastage (Amt)</th>
            <th>Quantity</th>
            <th>Orginal Price</th>
            <th>Total</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          
        {billItem.map((item, key)=>{return(
          <tr key={key}>
            <td >{key+1}</td>
            <td>{item.productName}</td>
            <td>{item.weight}</td>
            <td>{item.wastageAmt || 0}</td>
            <td>{item.quantity}</td>
            <td>{item.orginal_price}</td>
            <td>{item.total}</td>
            <td><button className="btn btn-sm btn-danger" onClick={() => handleDeleteItem(key)}>Delete</button></td>
          </tr>
            )})}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan="5" className="text-end fw-bold">Sub Total Amount</td>
            <td colSpan="2">
              ₹ {billItem.reduce((acc, item) => acc + parseFloat(item.total || 0), 0).toFixed(2)}
            </td>
          </tr>
          <tr>
            <td colSpan="5" className="text-end fw-bold">Advance Amount</td>
            <td colSpan="2">
              <input
                type="number"
                step="0.01"
                className="form-control"
                value= {advanceAmount}
                onChange={(e) => setAdvanceAmount(e.target.value)}
              />
            </td>
          </tr>
          <tr>
            <td colSpan="5" className="text-end fw-bold">Total Amount</td>
            <td colSpan="2">
              <input
                type="number"
                step="0.01"
                className="form-control"
                value={customTotalAmount === '' ? (billItem.reduce((acc, item) => acc + parseFloat(item.total || 0), 0) - advanceAmount).toFixed(2) : customTotalAmount}
                onChange={(e) => setCustomTotalAmount(e.target.value)}
                placeholder="Total Amount"
              />
            </td>
          </tr>
          <tr>
            <td colSpan="7" className="text-end">
              
              <button className="btn btn-success" onClick={handleBillSubmit} disabled={billItem.length <= 0 }>
                Submit
              </button>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export default Billing;
